import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, Star, Loader2, CheckCircle2, ArrowLeft, Inbox } from "lucide-react";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CATEGORIES = [
  "Overall Experience", "App/UI", "Ask AI", "Payments", "Loans",
  "Credit Card", "Rewards", "Support", "Performance", "Feature Request", "Other",
];

const empty = { rating: 0, category: "Overall Experience", text: "", feature_request: "" };
const Stars = ({ n }) => (<span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={12} className={i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}</span>);

export default function FeedbackWidget() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("form"); // form | mine
  const [f, setF] = useState(empty);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [mine, setMine] = useState(null);

  const reset = () => { setF(empty); setHover(0); setDone(false); setView("form"); setMine(null); };
  const onOpenChange = (v) => {
    if (!v && window.history.state?.zpFeedback) { window.history.back(); return; }
    setOpen(v); if (!v) setTimeout(reset, 200);
  };
  // Mobile: browser/hardware Back closes the modal instead of leaving the page.
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ zpFeedback: true }, "");
    const onPop = () => { setOpen(false); setTimeout(reset, 200); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);

  const loadMine = () => { setView("mine"); setMine(null); api.get("/feedback/mine").then((r) => setMine(r.data)).catch(() => setMine([])); };

  const submit = async () => {
    if (busy) return;
    if (f.rating < 1) { toast.error("Please select a star rating."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        rating: f.rating, category: f.category,
        text: f.text.trim() || null, feature_request: f.feature_request.trim() || null,
        page: loc.pathname,
      });
      setDone(true);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-fab" aria-label="Share feedback"
        className="fixed bottom-20 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-[#0B132B] px-3 py-3 text-sm font-semibold text-white shadow-lg ring-1 ring-white/10 transition-all hover:bg-[#14B8A6] hover:shadow-xl active:scale-95 sm:px-4 lg:bottom-6 lg:right-6">
        <MessageSquarePlus size={18} className="shrink-0" />
        <span className="hidden sm:inline">Share Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md overflow-hidden gap-0 rounded-3xl p-0" data-testid="feedback-modal">
          {done ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center" data-testid="feedback-success">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 size={30} /></span>
              <DialogTitle className="font-display text-xl font-bold text-[#0B132B]">Thank you!</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Your feedback helps us make ZRo Pe better.</DialogDescription>
              <button onClick={() => onOpenChange(false)} data-testid="feedback-done"
                className="mt-2 rounded-full bg-[#14B8A6] px-6 py-2 text-sm font-semibold text-white hover:bg-[#0B132B]">Done</button>
            </div>
          ) : view === "mine" ? (
            <div className="max-h-[80vh] overflow-y-auto p-6" data-testid="feedback-mine">
              <div className="flex items-center gap-2">
                <button onClick={() => setView("form")} data-testid="feedback-mine-back" className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-100"><ArrowLeft size={16} /></button>
                <DialogTitle className="font-display text-lg font-bold text-[#0B132B]">Your feedback</DialogTitle>
              </div>
              <DialogDescription className="sr-only">Your past feedback and responses from the team</DialogDescription>
              <div className="mt-4 space-y-3">
                {mine === null ? <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
                  : mine.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center text-slate-400"><Inbox size={26} /><p className="text-sm">You haven't shared feedback yet.</p></div>
                  ) : mine.map((m) => (
                    <div key={m.id} className="rounded-lg border border-slate-200 p-3" data-testid={`feedback-mine-item-${m.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2"><Stars n={m.rating} /><span className="text-xs font-semibold text-slate-500">{m.category}</span></div>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.status === "Resolved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{m.status}</span>
                      </div>
                      {m.text && <p className="mt-1.5 text-sm text-[#0B132B]">{m.text}</p>}
                      <p className="mt-1 text-[11px] text-slate-400">{fmtDateTime(m.created_at)}</p>
                      {m.admin_reply && (
                        <div className="mt-2 rounded-md border border-[#14B8A6]/30 bg-[#14B8A6]/5 px-3 py-2" data-testid={`feedback-mine-reply-${m.id}`}>
                          <p className="text-[11px] font-semibold text-[#14B8A6]">ZRo Pe Support</p>
                          <p className="text-sm text-[#0B132B]">{m.admin_reply}</p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="max-h-[92vh] overflow-y-auto" data-testid="feedback-form">
              {/* premium beach hero */}
              <div className="relative">
                <img src="/brand/feedback_hero.jpg" alt="ZRo Pe — we value your feedback" className="h-72 w-full object-cover object-center sm:h-80" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white to-transparent" />
              </div>
              {/* form card */}
              <div className="relative -mt-6 rounded-t-3xl bg-white px-6 pb-6 pt-5">
                <DialogTitle className="text-center font-display text-xl font-bold text-[#0B132B]">Share your feedback</DialogTitle>
                <DialogDescription className="text-center text-xs text-slate-500">Tell us how we're doing — it only takes a moment.</DialogDescription>

                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-semibold text-[#0B132B]">Your rating</label>
                    <div className="flex gap-1" data-testid="feedback-stars">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} type="button" data-testid={`feedback-star-${n}`}
                          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                          onClick={() => setF({ ...f, rating: n })}
                          className="transition-transform hover:scale-110" aria-label={`${n} star`}>
                          <Star size={28} className={(hover || f.rating) >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-[#0B132B]">Category</label>
                    <select data-testid="feedback-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                      className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#0B132B] outline-none focus:border-[#14B8A6]">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-[#0B132B]">Your feedback</label>
                    <textarea data-testid="feedback-text" rows={3} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })}
                      placeholder="What did you like or what can we improve?"
                      className="mt-1.5 w-full resize-none rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-[#0B132B] outline-none focus:border-[#14B8A6]" />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-[#0B132B]">Feature request <span className="font-normal text-slate-400">(optional)</span></label>
                    <input data-testid="feedback-feature" value={f.feature_request} onChange={(e) => setF({ ...f, feature_request: e.target.value })}
                      placeholder="Something you'd love to see?"
                      className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-[#0B132B] outline-none focus:border-[#14B8A6]" />
                  </div>

                  <button onClick={submit} disabled={busy} data-testid="feedback-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#14B8A6] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0B132B] disabled:opacity-60">
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Submit feedback"}
                  </button>
                  <button onClick={loadMine} data-testid="feedback-mine-link" className="w-full text-center text-xs font-semibold text-slate-500 hover:text-[#14B8A6]">View my past feedback & responses</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
